import React from 'react';
import type { GradingTab } from './types';
import { formatGradingScore } from './utils';
import { btnLink } from '../adminUi';

const TAB_ITEMS: { id: GradingTab; label: string }[] = [
  { id: 'tree', label: 'Дерево грейдинга' },
  { id: 'calibration', label: 'Калибровка' },
  { id: 'factors', label: 'Дифференцирующие факторы' },
  { id: 'questions', label: 'Вопросы и ответы' },
  { id: 'roles', label: 'Роли и грейды' },
  { id: 'preview', label: 'Предпросмотр анкеты' },
];

export function GradingTabs({ tab, onTab }: { tab: GradingTab; onTab: (t: GradingTab) => void }) {
  return (
    <div className="dc-grading-tabs" role="tablist">
      {TAB_ITEMS.map((item) => (
        <button
          key={item.id}
          type="button"
          role="tab"
          aria-selected={tab === item.id}
          className={`dc-grading-tab ${tab === item.id ? 'dc-grading-tab-active' : ''}`}
          onClick={() => onTab(item.id)}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

export function GradingMetaLine({ items }: { items: string[] }) {
  return <p className="dc-grading-meta">{items.join(' · ')}</p>;
}

export function GradingContextPath({ path }: { path: string }) {
  if (!path) return null;
  return (
    <p className="dc-grading-context-path">
      <i className="fas fa-sitemap text-xs" aria-hidden="true" /> {path}
    </p>
  );
}

export function GradingEmptyState({ children }: { children: React.ReactNode }) {
  return <div className="dc-grading-empty">{children}</div>;
}

export function GradingColumnHeader({ title, action }: { title: string; action?: React.ReactNode }) {
  return (
    <div className="dc-grading-col-header">
      <h3>{title}</h3>
      {action}
    </div>
  );
}

export function GradingScoreRange({ min, max }: { min: number | null; max: number | null }) {
  if (min == null && max == null) return <span className="text-slate-400">—</span>;
  return (
    <span className="dc-grading-score-range">
      {formatGradingScore(min)} – {formatGradingScore(max)} баллов
    </span>
  );
}

export function GradingHeaderActions({ primaryLabel, onPrimary, secondaryLabel, onSecondary }: {
  primaryLabel: string; onPrimary: () => void; secondaryLabel?: string; onSecondary?: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {secondaryLabel && onSecondary && (
        <button type="button" className="dc-btn-secondary" onClick={onSecondary}>{secondaryLabel}</button>
      )}
      <button type="button" className="dc-btn-primary" onClick={onPrimary}>{primaryLabel}</button>
    </div>
  );
}

export function GradingTypeBadge({ label, variant = 'neutral' }: { label: string; variant?: 'calibration' | 'scoring' | 'neutral' | 'hidden' }) {
  return <span className={`dc-grading-type-badge dc-grading-type-${variant}`}>{label}</span>;
}

export function GradingVisibilityBadge({ isVisible }: { isVisible?: boolean }) {
  if (isVisible !== false) return null;
  return <GradingTypeBadge label="Скрыто" variant="hidden" />;
}

export function GradingActionsMenu({ onEdit, onDelete }: { onEdit: () => void; onDelete?: () => void; onDuplicate?: () => void; onHide?: () => void }) {
  return (
    <div className="flex gap-2">
      <button type="button" className={`${btnLink} text-xs`} onClick={(e) => { e.stopPropagation(); onEdit(); }}>
        Редактировать
      </button>
      {onDelete && (
        <button type="button" className="text-xs text-red-600 hover:text-red-800" onClick={(e) => { e.stopPropagation(); onDelete(); }}>
          Удалить
        </button>
      )}
    </div>
  );
}

export function GradingDeleteButton({ label, onClick, disabled }: { label?: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50" onClick={onClick} disabled={disabled}>
      {label || 'Удалить'}
    </button>
  );
}
